export type ToastDomain =
  | 'generic'
  | 'auth'
  | 'farm'
  | 'area'
  | 'paddock'
  | 'lot'
  | 'animal'
  | 'nutrition'
  | 'inventory'
  | 'program'
  | 'item'
  | 'supplier'
  | 'buyer'
  | 'finance'
  | 'report';

export type ToastAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'save'
  | 'fetch'
  | 'move'
  | 'transfer'
  | 'import'
  | 'calculate'
  | 'finalize'
  | 'login'
  | 'read'
  | 'list';

export type ToastMessageContext = {
  domain?: ToastDomain;
  action?: ToastAction;
};

type CatalogEntry = {
  title: string;
  actionHint: string;
};

const DEFAULT_ENTRY: CatalogEntry = {
  title: 'N\u00e3o foi poss\u00edvel concluir a opera\u00e7\u00e3o',
  actionHint: 'Verifique os dados e tente novamente.',
};

const entry = (title: string, actionHint: string): CatalogEntry => ({ title, actionHint });

const CATALOG: Partial<Record<`${ToastDomain}.${ToastAction}`, CatalogEntry>> = {
  'auth.login': entry('N\u00e3o foi poss\u00edvel entrar na conta', 'Confirme seu e-mail e senha e tente novamente.'),
  'farm.update': entry('N\u00e3o foi poss\u00edvel atualizar a fazenda', 'Revise os dados e tente novamente.'),
  'farm.create': entry('N\u00e3o foi poss\u00edvel cadastrar a fazenda', 'Revise os dados obrigat\u00f3rios e tente novamente.'),
  'farm.save': entry('N\u00e3o foi poss\u00edvel salvar a fazenda', 'Revise os dados e tente novamente.'),
  'area.create': entry('N\u00e3o foi poss\u00edvel cadastrar a \u00e1rea', 'Revise os dados da \u00e1rea e tente novamente.'),
  'area.update': entry('N\u00e3o foi poss\u00edvel atualizar a \u00e1rea', 'Revise os dados da \u00e1rea e tente novamente.'),
  'area.delete': entry('N\u00e3o foi poss\u00edvel remover a \u00e1rea', 'Verifique se h\u00e1 v\u00ednculos e tente novamente.'),
  'paddock.create': entry('N\u00e3o foi poss\u00edvel cadastrar o piquete', 'Revise os dados do piquete e tente novamente.'),
  'paddock.update': entry('N\u00e3o foi poss\u00edvel atualizar o piquete', 'Revise os dados do piquete e tente novamente.'),
  'paddock.delete': entry('N\u00e3o foi poss\u00edvel remover o piquete', 'Verifique se h\u00e1 v\u00ednculos e tente novamente.'),
  'lot.create': entry('N\u00e3o foi poss\u00edvel cadastrar o lote', 'Revise os dados do lote e tente novamente.'),
  'lot.update': entry('N\u00e3o foi poss\u00edvel atualizar o lote', 'Revise os dados do lote e tente novamente.'),
  'lot.delete': entry('N\u00e3o foi poss\u00edvel remover o lote', 'Verifique se h\u00e1 v\u00ednculos e tente novamente.'),
  'lot.transfer': entry('N\u00e3o foi poss\u00edvel transferir o lote', 'Verifique origem e destino e tente novamente.'),
  'lot.move': entry('N\u00e3o foi poss\u00edvel mover o lote', 'Verifique origem e destino e tente novamente.'),
  'animal.create': entry('N\u00e3o foi poss\u00edvel cadastrar o animal', 'Revise os dados do animal e tente novamente.'),
  'animal.update': entry('N\u00e3o foi poss\u00edvel atualizar o animal', 'Revise os dados e tente novamente.'),
  'animal.transfer': entry('N\u00e3o foi poss\u00edvel transferir o animal', 'Verifique origem e destino e tente novamente.'),
  'animal.import': entry('N\u00e3o foi poss\u00edvel importar os animais', 'Revise o arquivo e tente novamente.'),
  'nutrition.create': entry('N\u00e3o foi poss\u00edvel cadastrar a nutri\u00e7\u00e3o', 'Revise os dados e tente novamente.'),
  'nutrition.update': entry('N\u00e3o foi poss\u00edvel atualizar a nutri\u00e7\u00e3o', 'Revise os dados e tente novamente.'),
  'nutrition.delete': entry('N\u00e3o foi poss\u00edvel remover a nutri\u00e7\u00e3o', 'Verifique se h\u00e1 v\u00ednculos e tente novamente.'),
  'inventory.create': entry('N\u00e3o foi poss\u00edvel registrar o estoque', 'Revise os dados e tente novamente.'),
  'inventory.update': entry('N\u00e3o foi poss\u00edvel atualizar o estoque', 'Revise os dados e tente novamente.'),
  'inventory.delete': entry('N\u00e3o foi poss\u00edvel remover o registro', 'Tente novamente em instantes.'),
  'program.create': entry('N\u00e3o foi poss\u00edvel criar o programa', 'Revise os dados e tente novamente.'),
  'program.update': entry('N\u00e3o foi poss\u00edvel atualizar o programa', 'Revise os dados e tente novamente.'),
  'program.delete': entry('N\u00e3o foi poss\u00edvel remover o programa', 'Verifique se h\u00e1 v\u00ednculos e tente novamente.'),
  'item.create': entry('N\u00e3o foi poss\u00edvel cadastrar o ingrediente', 'Revise os dados e tente novamente.'),
  'item.update': entry('N\u00e3o foi poss\u00edvel atualizar o ingrediente', 'Revise os dados e tente novamente.'),
  'item.delete': entry('N\u00e3o foi poss\u00edvel remover o ingrediente', 'Verifique se h\u00e1 v\u00ednculos e tente novamente.'),
  'supplier.create': entry('N\u00e3o foi poss\u00edvel cadastrar o fornecedor', 'Revise os dados e tente novamente.'),
  'supplier.update': entry('N\u00e3o foi poss\u00edvel atualizar o fornecedor', 'Revise os dados e tente novamente.'),
  'supplier.delete': entry('N\u00e3o foi poss\u00edvel remover o fornecedor', 'Verifique se h\u00e1 v\u00ednculos e tente novamente.'),
  'buyer.create': entry('N\u00e3o foi poss\u00edvel cadastrar o comprador', 'Revise os dados e tente novamente.'),
  'buyer.update': entry('N\u00e3o foi poss\u00edvel atualizar o comprador', 'Revise os dados e tente novamente.'),
  'buyer.delete': entry('N\u00e3o foi poss\u00edvel remover o comprador', 'Verifique se h\u00e1 v\u00ednculos e tente novamente.'),
  'finance.update': entry('N\u00e3o foi poss\u00edvel atualizar a venda', 'Revise os dados e tente novamente.'),
  'finance.delete': entry('N\u00e3o foi poss\u00edvel remover a venda', 'Tente novamente em instantes.'),
  'report.create': entry('N\u00e3o foi poss\u00edvel gerar o relat\u00f3rio', 'Verifique os filtros e tente novamente.'),
};

export function getCatalogEntry(context?: ToastMessageContext): CatalogEntry {
  if (!context?.domain || !context.action) {
    return DEFAULT_ENTRY;
  }

  return CATALOG[`${context.domain}.${context.action}`] || DEFAULT_ENTRY;
}
